/**
 * OutstandingInvoicesWidget Component - T142
 *
 * Widget showing outstanding/overdue invoices
 */
import { Link } from '@tanstack/react-router';
import { GlassPanel } from '../ui';
import { formatCurrency, formatDate } from '@armard/shared';
import { useOutstandingInvoices } from '../../hooks/useDashboard';

interface OutstandingInvoicesWidgetProps {
  limit?: number;
  className?: string;
}

export function OutstandingInvoicesWidget({ limit = 5, className = '' }: OutstandingInvoicesWidgetProps) {
  const { data, isLoading, error } = useOutstandingInvoices(limit);

  if (isLoading) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-white/10 rounded w-1/3" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-white/5 rounded" />
            ))}
          </div>
        </div>
      </GlassPanel>
    );
  }

  if (error) {
    return (
      <GlassPanel className={`p-4 ${className}`}>
        <p className="text-red-400 text-center py-4">Fehler beim Laden</p>
      </GlassPanel>
    );
  }

  const totalOutstanding = data?.reduce((sum, inv) => sum + inv.grossTotal, 0) || 0;

  return (
    <GlassPanel className={`p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white">Offene Posten</h3>
        <Link
          to="/invoices"
          search={{ status: 'sent' }}
          className="text-sm text-white/50 hover:text-white"
        >
          Alle →
        </Link>
      </div>

      {/* Total */}
      <p className="text-2xl font-semibold text-white mb-4">{formatCurrency(totalOutstanding)}</p>

      {/* List */}
      {data && data.length > 0 ? (
        <div className="space-y-2">
          {data.map((invoice) => (
            <Link
              key={invoice.id}
              to="/invoices/$id"
              params={{ id: invoice.id }}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{invoice.invoiceNumber}</p>
                <p className="text-xs text-white/40 truncate">{invoice.customerName}</p>
              </div>
              <div className="text-right ml-3">
                <p className="text-sm text-white">{formatCurrency(invoice.grossTotal)}</p>
                <p
                  className={`text-xs ${
                    invoice.daysOverdue > 0 ? 'text-red-400' : 'text-white/40'
                  }`}
                >
                  {invoice.daysOverdue > 0
                    ? `${invoice.daysOverdue} Tage überfällig`
                    : `Fällig: ${formatDate(invoice.dueDate)}`}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-white/40 text-center py-4">Keine offenen Posten</p>
      )}
    </GlassPanel>
  );
}
